/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";

import { Button } from "../components/Button";
import { DateRangeType } from "../components/DateRangePicker";
import { ListAndFiltersPageBodyContainer, PageContainer } from "../components/layout/PageContainer";
import Section from "../components/layout/Section";
import { Loader } from "../components/Loader";
import { PaginationCounter } from "../components/Pagination/PaginationCounter";
import PaginationDetails from "../components/Pagination/PaginationDetails";
import TitleBox from "../components/TitleBox";
import Typography from "../components/Typography";
import { selectCurrentFarmerAccount } from "../features/azure-b2c/storage";
import { NoOrders } from "../features/orders/NoOrders";
import { MAX_ORDERS_PAGE_SIZE } from "../features/orders/utils/consts";
import QuoteList from "../features/quotes/components/QuotesList/QuoteList";
import {
  argToHash,
  getQuotes,
  selectQuote,
  selectQuoteStatus,
} from "../features/quotes/storage/quotes.slice";
import { Quote, QuotesRequestCache } from "../features/quotes/storage/types";
import { useAppDispatch } from "../store/rootStore";
import { TestIds } from "../testIds";
import { useScrollRestoration } from "../util/useScrollRestoration";

const initialDateRange: DateRangeType = {
  to: undefined,
  from: undefined,
};

const Quotes = () => {
  const account = useSelector(selectCurrentFarmerAccount);
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLElement>(null);
  useScrollRestoration({
    ref,
    scrollYPositionKey: sessionStorage.scrollYPositionOnQuotes,
    path: "/quotes/",
  });

  // Extract query parameters from the URL
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const initialSearchText = queryParams.get("searchText") ?? "";
  const initialExpiring = queryParams.get("expiring") ?? "false";
  const initialFromDate = queryParams.get("fromDate") ?? undefined;
  const initialToDate = queryParams.get("toDate") ?? undefined;

  const [searchText, setSearchText] = React.useState<string>(initialSearchText);
  const [expiringSoon, setExpiringSoon] = React.useState<boolean>(initialExpiring === "true");

  const [dateRange, setDateRange] = React.useState<DateRangeType>({
    from: initialFromDate ? new Date(initialFromDate) : undefined,
    to: initialToDate ? new Date(initialToDate) : undefined,
  });

  const isFilterApplied = React.useMemo(() => {
    return searchText !== "" || dateRange.to !== undefined || dateRange.from !== undefined;
  }, [searchText, dateRange]);

  const clearFilters = () => {
    setSearchText("");
    setDateRange(initialDateRange);
    setExpiringSoon(false);
  };

  const { hash, obj } = React.useMemo(() => {
    const obj = {
      startDateTime: dateRange.from,
      endDateTime: dateRange.to,
      salesId: searchText,
      expiringSoon,
      accountNumber: account?.accountNumber ?? "",
    };
    const hash = argToHash(obj);
    return {
      hash,
      obj,
    };
  }, [dateRange, searchText, expiringSoon, account]);

  const requestStatus: QuotesRequestCache | null = useSelector(selectQuoteStatus(hash));
  const quotes: Quote[] = useSelector(selectQuote(hash));

  const [timer, setTimer] = React.useState<NodeJS.Timeout>();

  const loadQuotes = () => {
    if (account) {
      clearTimeout(timer);
      setTimer(
        setTimeout(() => {
          dispatch(
            getQuotes({
              startDateTime: dateRange.from,
              endDateTime: dateRange.to,
              salesId: searchText,
              expiringSoon,
              accountNumber: account?.accountNumber ?? "",
            })
          );
        }, 1000)
      );
    }
  };

  const loadMore = () => {
    if (account) {
      if (requestStatus?.status !== "pending" && !requestStatus?.last) {
        dispatch(
          getQuotes({
            startDateTime: dateRange.from,
            endDateTime: dateRange.to,
            skip: quotes.length,
            top: MAX_ORDERS_PAGE_SIZE,
            salesId: searchText,
            expiringSoon,
            accountNumber: account?.accountNumber ?? "",
          })
        );
      }
    }
  };

  useEffect(() => {
    if (hash !== "" && !!account && obj.accountNumber === account?.accountNumber) {
      loadQuotes();
    } else if (obj.accountNumber === account?.accountNumber) {
      loadMore();
    }
  }, [hash]);

  const paginationCounter = React.useMemo(() => {
    if (
      quotes.length === 0 &&
      requestStatus?.status !== "rejected" &&
      requestStatus?.status !== "fulfilled"
    ) {
      return null;
    }
    return (
      <PaginationDetails
        showClear={isFilterApplied}
        onClear={clearFilters}
        paginationCounter={
          <PaginationCounter
            data-testid={TestIds.feat.quotes.list.paginationCounter}
            count={quotes.length}
            currentPage={1}
            itemsPerPage={quotes.length}
            label="quotes"
          />
        }
      />
    );
  }, [requestStatus, quotes, isFilterApplied, clearFilters]);

  // Update URL query parameters when searchText or dateRange changes
  useEffect(() => {
    const newQueryParams = new URLSearchParams();
    newQueryParams.set("searchText", searchText);
    if (dateRange.from) {
      newQueryParams.set("fromDate", dateRange.from.toISOString());
    }
    if (dateRange.to) {
      newQueryParams.set("toDate", dateRange.to.toISOString());
    }
    if (expiringSoon) {
      newQueryParams.set("pickup", expiringSoon.toString());
    }
    navigate({ search: newQueryParams.toString() });
  }, [searchText, dateRange, expiringSoon, history]);

  const footer = React.useMemo(() => {
    if (!requestStatus || requestStatus?.status === "pending") {
      return <Loader title="Loading your quotes" type="inline-flex" />;
    }

    if (requestStatus?.last) {
      if (quotes.length > 0) {
        return (
          <Typography variant="body1" color={"grey700"}>
            No more quotes to display
          </Typography>
        );
      } else {
        return (
          <Box width={"100%"} sx={{ marginY: -2 }}>
            <Typography variant="body1" color={"grey800"}>
              There are no quotes matching your search. Try adjusting your filters to broaden your
              search.
            </Typography>
          </Box>
        );
      }
    }

    if (requestStatus?.status === "rejected") {
      const center = quotes?.length > 0;
      return (
        <Box
          width={"100%"}
          alignItems={center ? "center" : "left"}
          display="flex"
          flexDirection={"column"}
          mt={-2.5}
          gap={1}
        >
          <Typography
            variant="body1"
            color={"error700"}
            align={center ? "center" : "left"}
            data-testid={TestIds.feat.quotes.list.loadMoreError}
          >
            Oops! Something went wrong loading your latest quotes.
            <br /> Please try again
          </Typography>

          <Button
            variant={center ? "text" : "contained"}
            onClick={loadMore}
            sx={{ flexShrink: 1, width: "min-content", whiteSpace: "nowrap" }}
            data-testid={TestIds.feat.quotes.list.loadMore}
          >
            {center ? "Load more quotes" : "Load quotes"}
          </Button>
        </Box>
      );
    }

    return (
      <Button variant="text" onClick={loadMore} data-testid={TestIds.feat.quotes.list.loadMore}>
        Load more quotes
      </Button>
    );
  }, [requestStatus, quotes, loadMore]);

  if (requestStatus?.status === "fulfilled" && !isFilterApplied && quotes.length === 0) {
    return (
      <PageContainer data-testid={TestIds.pages.quotes} sx={{ pb: "100px" }}>
        <Section pb={1} mb={3}>
          <TitleBox display="Quotes" />
        </Section>
        <NoOrders
          bodyText={
            "You do not currently have any quotes to display. Your quote will be displayed here once you've received a quote from Farmlands."
          }
          bodyPropsSx={{ marginBottom: 3 }}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer data-testid={TestIds.pages.quotes} sx={{ pb: "100px" }}>
      <Section pb={1} mb={1}>
        <TitleBox display="Quotes" />
      </Section>

      <ListAndFiltersPageBodyContainer>
        <Box flex={1}>
          {paginationCounter}
          <QuoteList data={quotes} />

          <Box marginY={4} width={"100%"} display="inline-flex" justifyContent={"center"}>
            {footer}
          </Box>
        </Box>
      </ListAndFiltersPageBodyContainer>
    </PageContainer>
  );
};

export default Quotes;
